import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addFieldsInfo, deletePostedLedgerAction, Ledger, updateProcessing } from '@store/calculations/ledgerSlice';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { InterimPayClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { setToNewZealandTimeZone } from 'src/utility/helper/dateHelper';

export interface State {
    isLoading: boolean;
    error: boolean;
    interimPaymentList: Ledger[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    interimPaymentList: [],
};

const interimPaySlice = createSlice({
    name: 'interimPay',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getInterimPaymentSuccess: (state, action: PayloadAction<Ledger[]>) => {
            state.interimPaymentList = action.payload;
            state.isLoading = false;
        },
        postInterimPaymentSuccess: (state) => {
            toast.success(stringResources.REDUX_SLICE.POST_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default interimPaySlice.reducer

// Actions
export const {
    startLoading,
    hasError,
} = interimPaySlice.actions;

// for useSelector
export const selectInterimPay = (state: RootState) => state.interimPay as State;

// ----------------------------------------------------------------------
export function getInterimPayment(fishingTripNumber: string, fromDate: Date, toDate: Date) {
    return async (dispatch, getState) => {
        dispatch(interimPaySlice.actions.startLoading());
        try {
            const client = new InterimPayClient(undefined, axios);
            fromDate = setToNewZealandTimeZone(fromDate);
            toDate = setToNewZealandTimeZone(toDate);
            const response = await client.estimateInterimPayment(fishingTripNumber, fromDate, toDate);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(interimPaySlice.actions.getInterimPaymentSuccess(payload));
        } catch (error) {
            dispatch(interimPaySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function postInterimPayment(fishingTripNumber: string, fromDate: Date, toDate: Date) {
    return async (dispatch, getState) => {
        dispatch(interimPaySlice.actions.startLoading());
        try {
            const client = new InterimPayClient(undefined, axios);
            fromDate = setToNewZealandTimeZone(fromDate);
            toDate = setToNewZealandTimeZone(toDate);
            await client.postInterimPayment(fishingTripNumber, fromDate, toDate);
            dispatch(interimPaySlice.actions.postInterimPaymentSuccess());
        } catch (error) {
            dispatch(interimPaySlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deletePostedInterimPayLedger(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(updateProcessing(true));
        const toastId = toast.loading(stringResources.REDUX_SLICE.DELETE_LOADING);
        try {
            const client = new InterimPayClient(undefined, axios);
            await client.deleteLedgerLines(exportId);
            dispatch(deletePostedLedgerAction(exportId));
            toast.update(toastId, {
                render: stringResources.REDUX_SLICE.DELETE_SUCCESS,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
        } catch (error) {
            dispatch(interimPaySlice.actions.hasError(true));
            handleError(error, toastId);
        }
    };
}